import { createTheme, ThemeOptions } from "@mui/material/styles";
import { colors, designSystem } from "./colors";

// Design System Défi Gourmand — maquette "design system" v1.2.0

const lightPalette = {
  mode: "light" as const,
  primary: colors.primary,
  secondary: colors.secondary,
  background: {
    default: designSystem.backgroundLight,
    paper: designSystem.ivory,
  },
  text: {
    primary: designSystem.textLight,
    secondary: designSystem.muted,
  },
  error: colors.error,
  warning: colors.warning,
  info: colors.info,
  success: colors.success,
  grey: colors.grey,
};

const darkPalette = {
  mode: "dark" as const,
  primary: colors.primary,
  secondary: colors.secondary,
  background: {
    default: designSystem.backgroundDark,
    paper: designSystem.surfaceDark,
  },
  text: {
    primary: designSystem.textDark,
    secondary: designSystem.muted,
  },
  error: colors.error,
  warning: colors.warning,
  info: colors.info,
  success: colors.success,
  grey: colors.grey,
};

const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: "2.25rem",
    fontWeight: 900,
    lineHeight: 1.2,
    letterSpacing: "-0.025em",
  },
  h2: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: "1.25rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  button: {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.875rem",
  },
  caption: {
    fontSize: "0.875rem",
    fontWeight: 400,
    fontStyle: "italic",
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
};

const shape = {
  borderRadius: 4, // 0.25rem design system
};

const defaultShadows = [
  "none",
  "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
  "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
  "0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)",
  "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)",
  "0px 4px 5px -2px rgba(0,0,0,0.2), 0px 7px 10px 1px rgba(0,0,0,0.14), 0px 2px 16px 1px rgba(0,0,0,0.12)",
  "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
  "0px 5px 6px -3px rgba(0,0,0,0.2), 0px 9px 12px 1px rgba(0,0,0,0.14), 0px 3px 16px 2px rgba(0,0,0,0.12)",
  "0px 6px 6px -3px rgba(0,0,0,0.2), 0px 10px 14px 1px rgba(0,0,0,0.14), 0px 4px 18px 3px rgba(0,0,0,0.12)",
  "0px 6px 7px -4px rgba(0,0,0,0.2), 0px 11px 15px 1px rgba(0,0,0,0.14), 0px 4px 20px 3px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 12px 17px 2px rgba(0,0,0,0.14), 0px 5px 22px 4px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 13px 19px 2px rgba(0,0,0,0.14), 0px 5px 24px 4px rgba(0,0,0,0.12)",
  "0px 7px 9px -4px rgba(0,0,0,0.2), 0px 14px 21px 2px rgba(0,0,0,0.14), 0px 5px 26px 4px rgba(0,0,0,0.12)",
  "0px 8px 9px -5px rgba(0,0,0,0.2), 0px 15px 22px 2px rgba(0,0,0,0.14), 0px 6px 28px 5px rgba(0,0,0,0.12)",
  "0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)",
  "0px 8px 11px -5px rgba(0,0,0,0.2), 0px 17px 26px 2px rgba(0,0,0,0.14), 0px 6px 32px 5px rgba(0,0,0,0.12)",
  "0px 9px 11px -5px rgba(0,0,0,0.2), 0px 18px 28px 2px rgba(0,0,0,0.14), 0px 7px 34px 6px rgba(0,0,0,0.12)",
  "0px 9px 12px -6px rgba(0,0,0,0.2), 0px 19px 29px 2px rgba(0,0,0,0.14), 0px 7px 36px 6px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2), 0px 20px 31px 3px rgba(0,0,0,0.14), 0px 8px 38px 7px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2), 0px 21px 33px 3px rgba(0,0,0,0.14), 0px 8px 40px 7px rgba(0,0,0,0.12)",
  "0px 10px 14px -6px rgba(0,0,0,0.2), 0px 22px 35px 3px rgba(0,0,0,0.14), 0px 8px 42px 7px rgba(0,0,0,0.12)",
  "0px 11px 14px -7px rgba(0,0,0,0.2), 0px 23px 36px 3px rgba(0,0,0,0.14), 0px 9px 44px 8px rgba(0,0,0,0.12)",
  "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
] as any;

const lightComponents: ThemeOptions["components"] = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: "10px 24px",
        fontSize: "0.875rem",
        fontWeight: 700,
        textTransform: "none",
        transition: "all 200ms ease",
        "&:active": { transform: "scale(0.98)" },
      },
      contained: {
        boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
      },
      containedPrimary: {
        backgroundColor: designSystem.primary,
        "&:hover": { backgroundColor: colors.primary.dark },
      },
      containedSecondary: {
        backgroundColor: designSystem.secondary,
        "&:hover": { backgroundColor: colors.secondary.dark },
      },
      outlined: {
        borderWidth: 2,
        "&:hover": { borderWidth: 2, backgroundColor: "rgba(28, 33, 24, 0.05)" },
      },
      text: {
        "&:hover": { backgroundColor: "rgba(28, 33, 24, 0.08)" },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
        border: `1px solid ${designSystem.borderLight}`,
        backgroundColor: designSystem.ivory,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: 8,
          backgroundColor: designSystem.ivory,
          "& fieldset": { borderColor: colors.grey[300] },
          "&:hover fieldset": { borderColor: designSystem.primary },
          "&.Mui-focused": {
            "& fieldset": {
              borderWidth: 2,
              borderColor: designSystem.primary,
            },
          },
          "&.Mui-error fieldset": { borderColor: designSystem.error },
        },
        "& .MuiInputLabel-root.Mui-focused": { color: designSystem.primary },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 9999,
        fontSize: "0.875rem",
        fontWeight: 700,
        height: "auto",
        padding: "4px 12px",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: "none" },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: "4px 8px",
        "&.Mui-selected": {
          backgroundColor: "rgba(45, 80, 22, 0.1)",
          color: designSystem.primary,
          "&:hover": { backgroundColor: "rgba(28, 33, 24, 0.15)" },
        },
        "&:hover": { backgroundColor: colors.grey[100] },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderColor: colors.grey[100], fontSize: "0.875rem" },
      head: {
        backgroundColor: colors.grey[50],
        fontWeight: 600,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: colors.grey[500],
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { "&:hover": { backgroundColor: colors.grey[50] } },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: designSystem.ivory,
        color: designSystem.textLight,
        borderBottom: `1px solid ${designSystem.borderLight}`,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: `1px solid ${designSystem.borderLight}`,
        backgroundColor: designSystem.ivory,
      },
    },
  },
};

const darkComponents: ThemeOptions["components"] = {
  ...lightComponents,
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: `1px solid rgba(255,255,255,0.05)`,
        backgroundColor: designSystem.surfaceDark,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          backgroundColor: designSystem.backgroundDark,
          "& fieldset": { borderColor: colors.grey[700] },
          "&:hover fieldset": { borderColor: designSystem.primary },
          "&.Mui-focused fieldset": {
            borderWidth: 2,
            borderColor: designSystem.primary,
          },
        },
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: "4px 8px",
        "&.Mui-selected": {
          backgroundColor: "rgba(45, 80, 22, 0.2)",
          color: designSystem.primary,
          "&:hover": { backgroundColor: "rgba(28, 33, 24, 0.25)" },
        },
        "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: designSystem.surfaceDark,
        color: colors.grey[400],
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: { "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" } },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: designSystem.surfaceDark,
        color: designSystem.textDark,
        borderBottom: `1px solid ${designSystem.borderDark}`,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: `1px solid ${designSystem.borderDark}`,
        backgroundColor: designSystem.surfaceDark,
      },
    },
  },
};

const lightThemeOptions: ThemeOptions = {
  palette: lightPalette,
  typography,
  shape,
  shadows: defaultShadows,
  components: lightComponents,
};

const darkThemeOptions: ThemeOptions = {
  palette: darkPalette,
  typography,
  shape,
  shadows: defaultShadows,
  components: darkComponents,
};

export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);
export const theme = lightTheme;
